import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  NumberTemplate,
  SwitchTemplate,
  TemplateWrapper,
} from "@ryde/controls";
import {
  getBulkBasePriceAdditionToggleFieldName,
  getFormLabelKey,
  IInjectedDrawerProps,
  IUpdateBasePriceProps,
  RydeUiUnit,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../common-default-drawer";
import {
  BaseDrawer,
  DrawerEntityDataLoader,
  DrawerFormWrapperStyled,
  DrawerHeader,
  FormFooter,
} from "../drawer";
import { UpdateBasePriceAdditionsBlockStyled } from "./update-base-price-additions.styled";
import { updateBasePriceDrawerForm } from "./update-base-price-drawer.models";
import { useUpdateBasePriceDrawer } from "./use-update-base-price-drawer";

const PRICE_RATE_ENTITY: RydeUiUnit = "priceRate";

export const UpdateBasePriceDrawer: FC<
  IUpdateBasePriceProps & IInjectedDrawerProps
> = ({ onCloseDrawer }) => {
  const { t } = useTranslation();
  const {
    methods,
    isSaveDisabled,
    onSaveClick,
    additionControls,
    isAdditionSchemaReady,
  } = useUpdateBasePriceDrawer();

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <CommonDefaultDrawer
        className="drawer-form"
        onCloseDrawer={onCloseDrawer}
        Header={
          <DrawerHeader
            title={t("priceRate.updateBasePrice")}
            onCloseDrawer={onCloseDrawer}
          />
        }
        Footer={
          <FormFooter
            cancelLabel={t("general.CLOSE")}
            onCancelClick={onCloseDrawer}
            onSaveClick={onSaveClick}
            isSaveDisabled={isSaveDisabled}
          />
        }
      >
        <FormProvider {...methods}>
          <DrawerFormWrapperStyled>
            <TemplateWrapper>
              <NumberTemplate
                name={updateBasePriceDrawerForm.percentageOfIncrease}
                formLabel={t("priceRate.percentageOfIncrease")}
              />
            </TemplateWrapper>
            <TemplateWrapper>
              <SwitchTemplate
                name={updateBasePriceDrawerForm.roundFinalPrice}
                formLabel={t("priceRate.roundFinalPrice")}
              />
            </TemplateWrapper>
            <UpdateBasePriceAdditionsBlockStyled>
              <div className="section-title">
                {t("priceRate.bulkIncreaseCurrencyAdditionsTitle")}
              </div>

              {!isAdditionSchemaReady ? (
                <DrawerEntityDataLoader
                  entityTitle={t(`${PRICE_RATE_ENTITY}.single`)}
                />
              ) : (
                additionControls.map(control => (
                  <TemplateWrapper key={control.id}>
                    <SwitchTemplate
                      name={getBulkBasePriceAdditionToggleFieldName(
                        control.name,
                      )}
                      formLabel={t(
                        getFormLabelKey(PRICE_RATE_ENTITY, control.name),
                      )}
                    />
                  </TemplateWrapper>
                ))
              )}
            </UpdateBasePriceAdditionsBlockStyled>
          </DrawerFormWrapperStyled>
        </FormProvider>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
